import React, { useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getYear } from 'date-fns';
import { sortBy } from 'lodash';
import useModal from '../../../../utils/hooks/useModal';
import SCOPE from '../../../../utils/gate/scope';
import withAuth from '../../../../utils/hocs/withAuth';
import withPermission from '../../../../utils/hocs/withPermission';
import withSidebar from '../../../../utils/hocs/withSidebar';
import PermissionsGate from '../../../../utils/gate/permissionsGate';
import seasonTypes from '../../../../constants/seasons';

// Services
import useSeasonService from '../../../../services/seasonService';

// Icons
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';

// Components
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import TabsToggle from '../../../molecules/TabsToggle/TabsToggle';
import SeasonsTable from '../../../tables/SeasonsTable/SeasonsTable';
import Button from '../../../atoms/Button/Button';
import SeasonCreationForm from './SeasonCreationForm';

function Seasons() {
  const { t } = useTranslation();

  const { getSeasons } = useSeasonService();
  const { displayModal } = useModal();

  const currentYear = getYear(new Date());
  const nextYear = currentYear + 1;
  const tabs = [
    { value: currentYear, label: t('seasons.currentYear') },
    { value: nextYear, label: t('seasons.nextYear') },
  ];

  const [seasons, setSeasons] = useState([]);
  const [selectedYear, setSelectedYear] = useState(tabs[0]);

  const seasonOptions = Object.values(seasonTypes).map((type) => (
    { value: type, label: t(`seasons.short.${type.toLowerCase()}`) }
  ));

  const { isFetching } = useQuery(['getSeasons'], getSeasons, {
    onSuccess: (res) => {
      setSeasons(res?.data?.seasons || []);
    },
  });

  const handleAddSeason = () => {
    displayModal({
      maxWidth: 330,
      title: t('seasons.add'),
      content: <SeasonCreationForm seasonOptions={seasonOptions} setSeasons={setSeasons} />,
    });
  };

  return (
    <PageWrapper
      title={t('seasons.title')}
      hasWhiteWrapper
    >
      {!isFetching && seasons?.length ? (
        <TabsToggle
          tabs={tabs}
          setActive={setSelectedYear}
          active={selectedYear}
          className="mobile-w-100 m-b-20"
        />
      ) : null}

      <SeasonsTable
        seasons={sortBy(seasons, 'startDate')}
        setSeasons={setSeasons}
        isFetching={isFetching}
        year={selectedYear?.value}
        seasonOptions={seasonOptions}
      />

      {!isFetching ? (
        <PermissionsGate scopes={[SCOPE.create_ownEst_season]}>
          <div className="m-t-20 d-flex a-center j-end sticky-actions mobile-a-stretch">
            <Button onClick={handleAddSeason}>
              <PlusIcon className="m-r-10" />
              {t('seasons.add')}
            </Button>
          </div>
        </PermissionsGate>
      ) : null}
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(Seasons),
    {
      scopes: [
        SCOPE.get_ownEst_season,
      ],
    },
  ),
);
